import { useEffect, useState } from 'react'

const situations = [
  {
    label: 'Crowded gym',
    prompt: 'The racks are taken and I have 12 minutes.',
    title: 'Crowded Gym Express',
    meta: '12 MIN • FULL BODY • DUMBBELLS',
    icon: 'groups',
    outcome: 'Keep moving with open equipment, short rests, and no waiting around.',
    highlights: [
      { icon: 'timer', title: 'Fits Your Time', detail: 'Useful sessions, even when rushed' },
      { icon: 'sync_alt', title: 'Easy Swaps', detail: 'Alternatives when stations are busy' },
    ],
    exercises: [
      { num: '01', name: 'Goblet Squats', detail: '3 Sets • 12 Reps' },
      { num: '02', name: 'DB Push Press', detail: '3 Sets • 10 Reps' },
      { num: '03', name: 'Renegade Rows', detail: '3 Sets • 8 Reps/side' },
    ],
  },
  {
    label: 'Mobility',
    prompt: 'My hip is cranky, but I still want to do something useful.',
    title: 'Mobility-Friendly Reset',
    meta: '18 MIN • LOW IMPACT • CONTROLLED',
    icon: 'self_improvement',
    outcome: 'Train around sensitive joints with slower reps, range you can own, and clear stop points.',
    highlights: [
      { icon: 'accessibility_new', title: 'Joint-Aware', detail: 'Keeps pressure off irritated areas' },
      { icon: 'speed_0_5', title: 'Controlled Pace', detail: 'More quality reps, less rushing' },
    ],
    exercises: [
      { num: '01', name: 'Supported Split Squat', detail: '2 Sets • 8 Reps/side' },
      { num: '02', name: 'Dead Bug Breathing', detail: '3 Sets • 6 Reps/side' },
      { num: '03', name: 'Band Pull-Aparts', detail: '2 Sets • 15 Reps' },
    ],
  },
  {
    label: 'Home short',
    prompt: 'I missed the gym and only have floor space at home.',
    title: 'Short Home Session',
    meta: '15 MIN • BODYWEIGHT • NO EQUIPMENT',
    icon: 'home',
    outcome: 'Turn a missed gym day into a quick session that keeps the habit alive.',
    highlights: [
      { icon: 'home', title: 'No Gear Needed', detail: 'Built around the space you have' },
      { icon: 'repeat', title: 'Habit Preserving', detail: 'Small win instead of skipping' },
    ],
    exercises: [
      { num: '01', name: 'Tempo Push-Ups', detail: '3 Sets • 8-12 Reps' },
      { num: '02', name: 'Reverse Lunges', detail: '3 Sets • 10 Reps/side' },
      { num: '03', name: 'Side Plank', detail: '2 Sets • 30 sec/side' },
    ],
  },
  {
    label: 'Full gym',
    prompt: 'I have a full hour and the gym is quiet.',
    title: 'Full Gym Strength Session',
    meta: '55 MIN • STRENGTH • BARBELL + MACHINES',
    icon: 'fitness_center',
    outcome: 'Use the room when you have it: heavier compounds, focused accessories, and enough rest.',
    highlights: [
      { icon: 'fitness_center', title: 'Uses The Setup', detail: 'Barbells, cables, and machines' },
      { icon: 'monitoring', title: 'Progress Focused', detail: 'Clear targets from recent work' },
    ],
    exercises: [
      { num: '01', name: 'Back Squat', detail: '4 Sets • 5 Reps' },
      { num: '02', name: 'Bench Press', detail: '4 Sets • 6 Reps' },
      { num: '03', name: 'Cable Row', detail: '3 Sets • 10 Reps' },
    ],
  },
]

const rotationDelayMs = 5500
const manualPauseMs = 12000

export function SituationalWorkoutsSection() {
  const [activeIndex, setActiveIndex] = useState(0)
  const [manualPauseUntil, setManualPauseUntil] = useState(0)
  const activeSituation = situations[activeIndex]

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return
    }

    const intervalId = window.setInterval(() => {
      if (Date.now() < manualPauseUntil) {
        return
      }

      setActiveIndex((currentIndex) => (currentIndex + 1) % situations.length)
    }, rotationDelayMs)

    return () => window.clearInterval(intervalId)
  }, [manualPauseUntil])

  const selectSituation = (index: number) => {
    setActiveIndex(index)
    setManualPauseUntil(Date.now() + manualPauseMs)
  }

  return (
    <section className="section-y section-x bg-surface-container-low">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 md:mb-12 text-center lg:text-left">
          <span className="text-secondary font-headline font-bold tracking-widest text-xs uppercase mb-4 block">
            Adaptive Planning
          </span>
          <h2 className="font-headline font-bold text-3xl sm:text-4xl md:text-5xl mb-4 sm:mb-6">
            Adapt Your Workout
          </h2>
          <p className="text-on-surface-variant max-w-2xl mx-auto lg:mx-0 text-base sm:text-lg">
            Busy gym? Short on time? Missing equipment? Tell gymAhead what changed and get a
            practical workout that still moves you forward.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 lg:grid-rows-[auto_1fr] gap-6 lg:gap-8 items-start">
          {/* Input: prompt + situation picker */}
          <div className="lg:col-span-2 order-1 space-y-4">
            <div className="p-4 sm:p-6 rounded-2xl bg-surface-container-lowest recessed-input">
              <p className="text-on-surface-variant italic mb-4 font-label text-sm leading-relaxed">
                &quot;{activeSituation.prompt}&quot;
              </p>
              <div className="flex justify-between items-center gap-3">
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-on-surface">
                  Adjusting today&apos;s workout
                </span>
                <span
                  className="material-symbols-outlined text-xl text-on-surface-variant shrink-0"
                  aria-hidden="true"
                >
                  tune
                </span>
              </div>
            </div>

            <div
              className="grid grid-cols-2 gap-2"
              role="tablist"
              aria-label="Choose a workout situation"
            >
              {situations.map((situation, index) => (
                <button
                  key={situation.label}
                  type="button"
                  role="tab"
                  aria-selected={activeIndex === index}
                  onClick={() => selectSituation(index)}
                  className={`rounded-full px-3 py-2 text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-colors text-center ${
                    activeIndex === index
                      ? 'bg-primary text-on-primary'
                      : 'bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high'
                  }`}
                >
                  {situation.label}
                </button>
              ))}
            </div>
          </div>

          {/* Output: adapted workout */}
          <div
            className="lg:col-span-3 lg:row-span-2 order-2 min-w-0"
            role="tabpanel"
            aria-live="polite"
            aria-label={activeSituation.title}
          >
            <article
              key={activeSituation.title}
              className="bg-surface-container-highest rounded-2xl sm:rounded-3xl p-5 sm:p-6 lg:p-8 motion-reduce:transition-none transition-opacity duration-300"
            >
              <div className="flex justify-between items-start gap-4 mb-5 sm:mb-6">
                <div className="min-w-0">
                  <h3 className="text-xl sm:text-2xl font-headline font-bold text-on-surface mb-1">
                    {activeSituation.title}
                  </h3>
                  <p className="text-on-surface-variant font-label text-[10px] sm:text-xs uppercase tracking-widest">
                    {activeSituation.meta}
                  </p>
                </div>
                <span
                  className="material-symbols-outlined text-3xl sm:text-4xl text-on-surface/20 shrink-0"
                  aria-hidden="true"
                >
                  {activeSituation.icon}
                </span>
              </div>

              <p className="text-on-surface-variant text-sm sm:text-base mb-5 sm:mb-6 leading-relaxed">
                {activeSituation.outcome}
              </p>

              <div className="space-y-3">
                {activeSituation.exercises.map((ex) => (
                  <div
                    key={ex.num}
                    className="flex items-center gap-3 sm:gap-5 p-3 sm:p-4 rounded-xl bg-surface-container-lowest min-w-0"
                  >
                    <span className="text-2xl sm:text-3xl font-headline font-black text-outline-variant shrink-0 tabular-nums">
                      {ex.num}
                    </span>
                    <div className="flex-grow min-w-0">
                      <p className="text-on-surface font-bold text-sm sm:text-base">{ex.name}</p>
                      <p className="text-on-surface-variant text-xs sm:text-sm">{ex.detail}</p>
                    </div>
                    <span
                      className="material-symbols-outlined text-on-surface-variant shrink-0"
                      aria-hidden="true"
                    >
                      chevron_right
                    </span>
                  </div>
                ))}
              </div>
            </article>
          </div>

          {/* Context highlights — below result on mobile, below picker on desktop */}
          <div className="lg:col-span-2 order-3 space-y-3">
            {activeSituation.highlights.map((highlight) => (
              <div
                key={highlight.title}
                className="p-4 rounded-xl bg-surface-container-high flex items-center gap-3 sm:gap-4 min-w-0"
              >
                <span className="material-symbols-outlined text-secondary shrink-0" aria-hidden="true">
                  {highlight.icon}
                </span>
                <div className="min-w-0">
                  <p className="text-on-surface font-bold text-sm">{highlight.title}</p>
                  <p className="text-on-surface-variant text-xs leading-relaxed">
                    {highlight.detail}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
